import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderText = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );

const getSessionId = () => {
  let id = localStorage.getItem("osh_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("osh_chat_session", id);
  }
  return id;
};

const STARTER = {
  role: "assistant",
  text: "Hey, I'm Harry's AI Coach. Ask me anything about training with Harry, his plans, or booking your free consultation.",
};

export default function AICoach() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([STARTER]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/history?session_id=${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMessages([STARTER, ...d.messages.map((m) => ({ role: m.role, text: m.text }))]);
      })
      .catch(() => {});
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finished = false;
      while (!finished) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") { finished = true; break; }
          try {
            const data = JSON.parse(payload);
            const chunk = data.text || data.error || "";
            if (chunk) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", text: next[next.length - 1].text + chunk };
                return next;
              });
            }
          } catch { /* partial chunk, skip */ }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", text: "Connection dropped. Try again or message us on WhatsApp." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-[60] flex flex-col items-start gap-4" data-testid="ai-coach-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="w-[calc(100vw-3rem)] max-w-sm md:w-96 border border-white/10 bg-zinc-950 shadow-2xl flex flex-col"
            data-testid="ai-coach-panel"
          >
            <div className="flex items-center justify-between bg-gold px-4 py-3">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-black" />
                <p className="font-display font-semibold uppercase tracking-[0.15em] text-black text-sm">AI Coach</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-coach-close-btn" aria-label="Close AI coach" className="text-black/70 hover:text-black transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={scrollRef} className="h-80 overflow-y-auto p-4 space-y-3" data-testid="ai-coach-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-gold text-black" : "bg-secondary text-zinc-200 border border-white/10"
                    }`}
                    data-testid={`ai-coach-message-${m.role}-${i}`}
                  >
                    {m.text ? renderText(m.text) : (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-gold" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 border-t border-white/10 p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="ai-coach-input"
                placeholder="Ask about training, plans, booking..."
                className="flex-1 bg-secondary/60 border border-white/10 text-white text-sm px-3 py-2.5 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-gold"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="ai-coach-send-btn"
                aria-label="Send message"
                className="w-10 h-10 shrink-0 bg-gold text-black flex items-center justify-center hover:bg-gold-bright transition-colors duration-300 disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-coach-fab"
        aria-label="Open AI coach chat"
        className="relative w-14 h-14 md:w-16 md:h-16 rounded-full border border-gold/60 bg-zinc-950 text-gold flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300 shadow-xl"
      >
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6 md:w-7 md:h-7" />}
      </button>
    </div>
  );
}
